"use client";

import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Typography, IconButton, Alert, AlertTitle, Collapse } from "@mui/material";
import Link from "next/link";
import { useState, useEffect } from "react";
import { colors } from "../../theme";

/**
 * Dismissible Auth Banner Component
 * 
 * A less dominant, user-friendly notification for unauthorized users
 * Features:
 * - Multiple design variants (info, warning, subtle)
 * - Dismissible with sessionStorage persistence (reappears on new browser session)
 * - Accessible keyboard navigation
 * - Responsive design
 */
const DismissibleAuthBanner = ({
  text,
  link = "/market-place",
  variant = "info",
  // "info" | "warning" | "subtle" | "minimal"
  storageKey = "authBannerDismissed"
}) => {
  const [isVisible, setIsVisible] = useState(true);

  // Check if user has previously dismissed the banner (session only)
  useEffect(() => {
    const dismissed = sessionStorage.getItem(storageKey);
    if (dismissed === "true") {
      setIsVisible(false);
    }
  }, [storageKey]);
  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem(storageKey, "true");
  };

  // Variant configurations
  const variants = {
    info: {
      severity: "info",
      icon: <InfoOutlinedIcon />,
      sx: {
        backgroundColor: "#E3F2FD",
        border: "1px solid #90CAF9",
        "& .MuiAlert-icon": {
          color: "#1976D2"
        }
      }
    },
    warning: {
      severity: "warning",
      icon: <WarningAmberIcon />,
      sx: {
        backgroundColor: "#FFF4E5",
        border: "1px solid #FFB74D",
        "& .MuiAlert-icon": {
          color: "#F57C00"
        }
      }
    },
    subtle: {
      severity: "info",
      icon: <InfoOutlinedIcon />,
      sx: {
        backgroundColor: colors.background.paper,
        border: `1px solid ${colors.border?.main || "#E0E0E0"}`,
        "& .MuiAlert-icon": {
          color: colors.cta.main
        }
      }
    },
    minimal: {
      severity: "info",
      icon: null,
      sx: {
        backgroundColor: "transparent",
        border: "none",
        boxShadow: "none"
      }
    }
  };
  const currentVariant = variants[variant] || variants.info;
  return <Collapse in={isVisible} data-sentry-element="Collapse" data-sentry-component="DismissibleAuthBanner" data-sentry-source-file="DismissibleAuthBanner.jsx">
      <Box width="96%" sx={{
      margin: '20px auto 10px',
      animation: isVisible ? 'slideDown 0.3s ease-out' : 'none',
      '@keyframes slideDown': {
        from: {
          opacity: 0,
          transform: 'translateY(-10px)'
        },
        to: {
          opacity: 1,
          transform: 'translateY(0)'
        }
      }
    }} data-sentry-element="Box" data-sentry-source-file="DismissibleAuthBanner.jsx">
        <Alert severity={currentVariant.severity} icon={currentVariant.icon} sx={{
        borderRadius: "8px",
        alignItems: "center",
        ...currentVariant.sx
      }} action={<IconButton aria-label="close" color="inherit" size="small" onClick={handleDismiss} sx={{
        opacity: 0.7,
        '&:hover': {
          opacity: 1
        }
      }}>
              <CloseIcon fontSize="small" />
            </IconButton>} data-sentry-element="Alert" data-sentry-source-file="DismissibleAuthBanner.jsx">
          <Link href={link} style={{
          textDecoration: "none",
          color: "inherit",
          display: "block"
        }} data-sentry-element="Link" data-sentry-source-file="DismissibleAuthBanner.jsx">
            <Typography variant="body2" sx={{
            fontSize: {
              xs: "13px",
              sm: "14px"
            },
            '&:hover': {
              textDecoration: "underline"
            }
          }} data-sentry-element="Typography" data-sentry-source-file="DismissibleAuthBanner.jsx">
              {text}
            </Typography>
          </Link>
        </Alert>
      </Box>
    </Collapse>;
};
export default DismissibleAuthBanner;